import type { FC, ReactElement } from 'react';
import React, { useContext } from 'react';

import type { ButtonProps } from '@atlaskit/button/standard-button';
import Button from '@atlaskit/button/standard-button';

import { VIEW_IN_PRODUCT_HELP_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

import { HELP_PANEL_ID, HELP_PANEL_WIDTH } from '../constants.ts';
import { useInProductHelpActions } from '../InProductHelpProvider.tsx';

export type InProductHelpButtonProps = ButtonProps & {
	articleId?: string;
	routeName?: string;
	routeGroup?: string;
	children?: ReactElement;
	articleTopic?: string;
	method?: 'context' | 'customEvent';
	onClick?: () => void;
};

export const InProductHelpButton: FC<InProductHelpButtonProps> = ({
	articleId = '',
	routeName = '',
	routeGroup = '',
	children,
	onClick,
	method = 'customEvent',
	articleTopic,
	...props
}) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const rightSidebarContext = useRightSidebarContext();
	const inProductHelpContext = useInProductHelpActions();

	const handleOnClick = () => {
		onClick && onClick();

		experienceTracker.start({
			name: VIEW_IN_PRODUCT_HELP_EXPERIENCE,
			attributes: {
				articleTopic,
			},
		});

		// We use customEvent usually for ReactPortal components
		if (method === 'customEvent') {
			document.dispatchEvent(
				new CustomEvent('openHelpPanel', {
					detail: {
						width: HELP_PANEL_WIDTH,
						panelId: HELP_PANEL_ID,
						articleId,
					},
					bubbles: true,
				}),
			);
		} else {
			inProductHelpContext.setArticleId(articleId);
			rightSidebarContext.open(HELP_PANEL_ID, HELP_PANEL_WIDTH);
		}
	};

	return (
		<Button {...props} appearance="link" spacing="none" onClick={handleOnClick}>
			{children}
		</Button>
	);
};
